import 'react-advanced-cropper/dist/style.css';

import type { ComponentProps, SubmitEventHandler } from 'react';
import type {
  AbstractCropperInstance,
  Coordinates,
  CropperInstanceSettings,
  CropperRef,
} from 'react-advanced-cropper';
import type { SwitchChangeFn } from '../../components/Switch/Switch';
import type {
  Media,
  MediaHydrated,
  Variant,
} from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cropper, RectangleStencil } from 'react-advanced-cropper';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import z from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import FlotiqPlugins from '../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { MediaConfigCropEvent } from '../../lib/flotiq-plugins/plugin-events/MediaConfigCropEvent';
import { EMPTY_ARRAY } from '../../lib/globals';
import { getCropAttributes, getTestProps } from '../../lib/helpers';

import ActionMenu from '../../components/ActionMenu/ActionMenu';
import Button from '../../components/Button/Button';
import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';
import HelpErrorTextsTemplate from '../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import Input from '../../components/Input/Input';
import Loader from '../../components/Loader/Loader';
import { PopoverPanelPlacement } from '../../components/Popover/classes';
import Switch from '../../components/Switch/Switch';
import Tooltip from '../../components/Tooltip/Tooltip';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import {
  CaretDownIcon,
  CenterIcon,
  DownloadCircleIcon,
  MaximizeIcon,
} from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const PRESETS = [
  {
    key: 'square',
    label: 'Square (1:1)',
    name: 'Square',
    ratio: 1,
  },
  {
    key: 'horizontal',
    label: 'Horizontal (16:9)',
    name: 'Horizontal',
    ratio: 16 / 9,
  },
  {
    key: 'standard',
    label: 'Standard (4:3)',
    name: 'Standard',
    ratio: 4 / 3,
  },
];

export type VariantModalConfig = { presets: typeof PRESETS };

const CustomLine = ({
  ref,
  cropper,
  ...props
}: ComponentProps<typeof RectangleStencil> & {
  cropper: AbstractCropperInstance<CropperInstanceSettings>;
}) => {
  const {
    width: stencilWidth = 0,
    left,
    top,
  } = cropper.getStencilCoordinates() || {};
  const { width = 0, height = 0 } = cropper.getCoordinates() || {};

  return (
    <>
      <RectangleStencil ref={ref} cropper={cropper} {...props} />
      <div
        className="absolute text-lime p-1 bg-blue m-1 transition-opacity	duration-500 pointer-events-none"
        style={{
          top,
          left,
          opacity: cropper.hasInteractions() ? 100 : 0,
        }}
      >
        {width.toFixed()} x {height.toFixed()},{' '}
        {((width / stencilWidth) * 100).toFixed()}%
      </div>
    </>
  );
};

interface VariantModalFormProps {
  /**
   * Media data
   */
  media: Media | MediaHydrated;
  /**
   * Variant data
   */
  variant?: Variant;
  /**
   * Currently existing variants names
   */
  variantsNames?: string[];
  /**
   * Media file name
   */
  fileName?: string;
  /**
   * Errors for variant
   */
  errors?: Record<string, string | string[]>;
  /**
   * Page test id
   */
  testId?: string;
}

const VariantModalForm = ({
  variant,
  variantsNames = EMPTY_ARRAY,
  media,
  errors,
  testId = '',
}: VariantModalFormProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const cropperRef = useRef<CropperRef>(null);
  const [coordinates, setCoordinates] = useState<
    Coordinates | null | undefined
  >();
  const [isLoading, setIsLoading] = useState(true);
  const [aspectRatio, setAspectRatio] = useState<number | undefined>();

  const config = useMemo(() => {
    const newConfig = {
      presets: PRESETS,
    };

    FlotiqPlugins.run(
      'flotiq.media.crop::config',
      new MediaConfigCropEvent({ config: newConfig }),
    );

    return newConfig;
  }, []);

  const validationSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .regex(/^\w+$/, t('MediaEdit.Variant.NameRegexp'))
          .min(1, t('Form.FormErrorNotBlank'))
          .refine(
            (value) =>
              variantsNames
                .filter((name) => name !== variant?.name)
                .indexOf(value) < 0,
            t('MediaEdit.Variant.UniqueName'),
          ),
      }),
    [t, variant?.name, variantsNames],
  );

  const onCancel = useCallback(() => {
    modalInstance.resolve?.();
  }, [modalInstance]);

  const initialCrop = useMemo(
    () => getCropAttributes(variant, media, true),
    [media, variant],
  );

  const center = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.setCoordinates(({ coordinates, imageSize }) => ({
        left: coordinates
          ? imageSize.width / 2 - coordinates.width / 2
          : imageSize.width / 2,
        top: coordinates
          ? imageSize.height / 2 - coordinates.height / 2
          : imageSize.height / 2,
      }));
    }
  }, []);

  const maximize = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.setCoordinates(({ imageSize }) => imageSize);
    }
  }, []);

  const onCropperChange = useCallback(() => {
    const coordinates = cropperRef.current?.getCoordinates();
    setCoordinates(coordinates);
  }, []);

  const onCoordinateInputChange = useCallback((type: string, value: string) => {
    if (cropperRef.current) {
      cropperRef.current.setCoordinates([
        ({ coordinates }) => ({
          ...coordinates,
          [type]: value || 0,
        }),
        ({ coordinates }) => {
          setCoordinates(coordinates);
          return coordinates;
        },
      ]);
    }
  }, []);

  const onDownload = useCallback(() => {
    const dataUrl = cropperRef.current?.getCanvas()?.toDataURL();

    const fileNameGroup = (media?.fileName || '').match(
      /(?<fileName>.*)\.(?<extenstion>[^.]+)$/,
    )?.groups;
    const fileName = fileNameGroup?.fileName || media.fileName || '';
    const name = fileName + '_crop';

    if (dataUrl) {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = name;
      link.click();
    }
  }, [media.fileName]);

  const toggleAspetRatio = useCallback<SwitchChangeFn>(
    (_, checked) => {
      if (checked && coordinates)
        setAspectRatio(coordinates.width / coordinates.height);
      else setAspectRatio(undefined);
    },
    [coordinates],
  );

  const settings = useMemo(
    () => [
      {
        onClick: maximize,
        tooltip: 'Maximize',
        icon: <MaximizeIcon className="w-4 text-slate-700 dark:text-white" />,
      },
      {
        onClick: center,
        tooltip: 'Center',
        icon: <CenterIcon className="w-4 text-slate-700 dark:text-white" />,
      },
      {
        onClick: onDownload,
        tooltip: 'Download',
        icon: <DownloadCircleIcon className="w-4 text-slate-700 dark:text-white" />,
      },
    ],
    [center, maximize, onDownload],
  );

  const initialValues = useMemo(
    () => ({ name: variant?.name || '' }),
    [variant?.name],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      const cropValues = cropperRef.current?.getCoordinates();
      const cropTransform = {
        trim: {
          top: cropValues?.top,
          left: cropValues?.left,
          width: cropValues?.width,
          height: cropValues?.height,
        },
      };

      const newValues = {
        name: value.name,
        ...cropTransform,
      };

      modalInstance.resolve?.(newValues);
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  useEffect(() => {
    if (Object.keys(errors || {}).length > 0) {
      setFormSubmitErrors(form, errors);
    }
  }, [errors, form]);

  const onAspectRatioChange = useCallback(
    (aspectRatio: { ratio: number; name: string }) => {
      setAspectRatio(aspectRatio.ratio);
      setTimeout(onCropperChange);

      if (!form.getFieldValue('name'))
        form.setFieldValue('name', aspectRatio.name);
    },
    [form, onCropperChange],
  );

  return (
    <form.AppForm>
      <form
        id="variant-form"
        className="space-y-3 w-full h-full"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <div className="flex items-end group">
          <form.AppField name="name">
            {(field) => (
              <field.Input
                label={t('Global.Name')}
                required
                additionalClasses="peer"
                additionalInputClasses="rounded-r-none border-r-0"
                additionalErrorClasses="mb-0"
                {...getTestProps(testId, 'name', 'testId')}
              />
            )}
          </form.AppField>

          <form.Subscribe
            selector={({ fieldMeta }) => {
              const meta = fieldMeta?.name;
              return meta?.isTouched ? meta?.errors?.length : null;
            }}
          >
            {(nameErrors) => (
              <ActionMenu
                menuItems={config?.presets || []}
                actionMenuPlacement={PopoverPanelPlacement.BottomRight}
                actionMenuPhonePlacement={PopoverPanelPlacement.BottomRight}
                button={<CaretDownIcon className="h-3 w-3" />}
                buttonColor="blueBordered"
                iconColor="blue"
                onClick={onAspectRatioChange}
                additionalClasses={twMerge(
                  'border border-slate-200 dark:border-slate-700 rounded-l-none',
                  nameErrors && 'mb-4 border-red',
                  'group-has-[input:focus]:border-blue',
                  'rounded-l-none px-4',
                )}
                additionalPopoverPanelClasses="whitespace-nowrap"
                {...getTestProps(testId, 'aspect-menu', 'testId')}
              />
            )}
          </form.Subscribe>
        </div>

        {isLoading && (
          <div
            className={twMerge(
              'absolute left-0 top-0 flex items-center justify-center',
              'h-full w-full pointer-events-none',
            )}
          >
            <Loader type="spinner-grid" />
          </div>
        )}

        {!isLoading && (
          <>
            <Switch
              name="aspect-ratio"
              checked={!!aspectRatio}
              label={t('MediaEdit.Variant.KeepRatio')}
              onChange={toggleAspetRatio}
              {...getTestProps(testId, 'aspect', 'testId')}
            />
            <details>
              <summary className="cursor-pointer hover:text-blue">
                {t('MediaEdit.Variant.Transform.Details')}
              </summary>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {['Top', 'Left', 'Width', 'Height'].map((coordinate) => {
                  const name = coordinate.toLocaleLowerCase();
                  return (
                    <Input
                      key={name}
                      name={name}
                      label={t(`MediaEdit.Variant.Transform.${coordinate}`)}
                      value={
                        coordinates && name in coordinates
                          ? coordinates[name as keyof typeof coordinates]
                          : 0
                      }
                      type="number"
                      onChange={(e) => {
                        onCoordinateInputChange(name, e.target.value);
                      }}
                      additionalInputClasses="p-1 pl-3 h-auto rounded-xs"
                      additionalInputLabelClasses="mb-0.5"
                      updateCurrent
                      {...getTestProps(testId, name, 'testId')}
                    />
                  );
                })}
              </div>
            </details>
          </>
        )}

        <div className="relative overflow-x-hidden">
          <Cropper
            ref={cropperRef}
            src={getMediaUrl(media)}
            className="max-w-full max-h-[calc(100vh-25rem)]! min-h-80 bg-transparent"
            onReady={() => setIsLoading(false)}
            defaultPosition={initialCrop}
            defaultSize={initialCrop}
            checkOrientation={false}
            stencilComponent={CustomLine}
            onChange={onCropperChange}
            {...(aspectRatio ? { aspectRatio: () => aspectRatio } : {})}
          />
          {!isLoading && (
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {/* We have access to maximaze and center functions only by calling cropper ref */}
              {/* eslint-disable-next-line react-hooks/refs */}
              {settings.map((button) => (
                <Tooltip
                  key={button.tooltip}
                  tooltip={t(`MediaEdit.Variant.Tooltip.${button.tooltip}`)}
                >
                  <Button
                    type="button"
                    buttonSize="lg"
                    onClick={button.onClick}
                    iconImage={button.icon}
                    buttonColor="borderless"
                    additionalClasses="p-2!"
                    {...getTestProps(testId, button.tooltip, 'testId')}
                  />
                </Tooltip>
              ))}
            </div>
          )}

          {errors?.transforms && (
            <HelpErrorTextsTemplate
              error={t('MediaEdit.Variant.Transform.Error')}
            />
          )}
        </div>
        <div
          className={twMerge(
            'w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5',
            'border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900',
          )}
        >
          <CancelButton
            onClick={onCancel}
            {...getTestProps(testId, 'cancel', 'testId')}
          />
          <SaveButton
            form="variant-form"
            disabled={isLoading}
            {...getTestProps(testId, 'save', 'testId')}
          />
        </div>

        <form.DirtyHandler dirty />
      </form>
    </form.AppForm>
  );
};

export default VariantModalForm;
